import {
    afterNextRender,
    ChangeDetectionStrategy,
    Component,
    computed,
    CUSTOM_ELEMENTS_SCHEMA,
    ElementRef,
    inject,
    model,
    ModelSignal,
    Signal,
    viewChild
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, PRIMARY_OUTLET, Router, RouterLink } from '@angular/router';
import { filter, map } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { Country } from '@shs-digi-care-monorepo/interfaces';
import '@shui/core/sh-popover';
import { EmeaFeatureService } from '../../services/emea-feature.service';
import { EmeaFeature } from '../../services/emea-feature.model';
import { SelectedCountryService } from '../../services/selected-country.service';
import {
    NO_ACTIVE_ITEM,
    REQUESTOR_REQUEST_ITEMS,
    SidebarNavItem,
    SUBTASK_ITEMS,
    TASK_OWNER_REQUEST_ITEMS
} from './sidebar.config';

@Component({
    selector: 'app-sidebar',
    templateUrl: './sidebar.component.html',
    styles: `
        .country-indicator-enter {
            animation: country-indicator-enter 200ms ease-in-out;
        }

        @keyframes country-indicator-enter {
            from {
                opacity: 0;
                transform: scale(0.6);
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RouterLink, TranslateModule],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    host: {
        '[style.flex]': '"0 0 auto"',
        '[style.width]': 'collapsed() ? collapsedWidth : expandedWidth',
        '[style.transition]': '"width 200ms ease-in-out"'
    }
})
export class SidebarComponent {
    private readonly router = inject(Router);
    private readonly countryScope = inject(SelectedCountryService);
    protected readonly features = inject(EmeaFeatureService);
    private readonly nav = viewChild.required<ElementRef<HTMLElement>>('nav');

    protected readonly collapsedWidth = '4.5rem';
    protected readonly expandedWidth = '300px';

    protected readonly requestItems: Signal<SidebarNavItem[]> = computed(() =>
        this.features.isTaskOwner() ? TASK_OWNER_REQUEST_ITEMS : REQUESTOR_REQUEST_ITEMS
    );
    protected readonly subtaskItems: Signal<SidebarNavItem[]> = computed(() =>
        this.features.isTaskOwner() ? SUBTASK_ITEMS : []
    );
    protected readonly canAccessAdminArea: Signal<boolean> = this.features.isEnabled(EmeaFeature.ADMIN_AREA);

    public readonly collapsed: ModelSignal<boolean> = model(true);
    protected readonly EmeaFeature = EmeaFeature;

    protected readonly countries: Signal<Country[]> = this.countryScope.countries;
    protected readonly selectedCountry: Signal<Country | null> = this.countryScope.selected;
    /** While the country switch is visible it hosts the collapse toggle instead of the sidebar. */
    protected readonly showCollapser: Signal<boolean> = computed(
        () => !this.countryScope.canSwitch() || this.collapsed()
    );
    protected readonly showCountryIndicator: Signal<boolean> = computed(
        () => this.countryScope.canSwitch() && this.collapsed()
    );
    protected readonly countryLabelKey: Signal<string> = computed(() => {
        const country = this.selectedCountry();
        return country ? `countries.${country.toLowerCase()}` : 'common.general.global';
    });

    private readonly url: Signal<string> = toSignal(
        this.router.events.pipe(
            filter((event): event is NavigationEnd => event instanceof NavigationEnd),
            map((event) => event.urlAfterRedirects)
        ),
        { initialValue: this.router.url }
    );

    constructor() {
        afterNextRender(() => void this.makeNavContentScrollable());
    }

    /**
     * `sh-vertical-nav` owns the active state of its items and resets it whenever
     * its slot changes, so the highlight has to be driven through `active-index`
     * instead of per-item `routerLinkActive` bindings.
     */
    protected readonly activeIndex: Signal<number> = computed(() => {
        const tree = this.router.parseUrl(this.url());
        const segments = tree.root.children[PRIMARY_OUTLET]?.segments.map((segment) => segment.path) ?? [];
        const requestItems = this.requestItems();

        const homeIndex = 0;
        const firstRequestIndex = 1;
        const createRequestIndex = firstRequestIndex + requestItems.length + this.subtaskItems().length;
        const hasPurchaseOrder = this.features.isActive(EmeaFeature.PURCHASE_ORDER);
        const purchaseOrderIndex = createRequestIndex + 1;
        const adminIndex = createRequestIndex + (hasPurchaseOrder ? 2 : 1);

        if (segments.length === 0) {
            return homeIndex;
        }
        if (segments[0] === 'requests') {
            const tab = tree.queryParams['tab'];
            const itemIndex = requestItems.findIndex((item) => item.tab === tab);
            if (itemIndex >= 0) {
                return firstRequestIndex + itemIndex;
            }
            const subtaskIndex = this.subtaskItems().findIndex((item) => item.tab === tab);
            return subtaskIndex >= 0 ? firstRequestIndex + requestItems.length + subtaskIndex : NO_ACTIVE_ITEM;
        }
        if (segments[0] === 'request' && segments.length === 1) {
            if (tree.queryParams['type'] === 'PURCHASE_ORDER') {
                return hasPurchaseOrder ? purchaseOrderIndex : NO_ACTIVE_ITEM;
            }
            return createRequestIndex;
        }
        if (segments[0] === 'admin' && this.canAccessAdminArea()) {
            return adminIndex;
        }
        return NO_ACTIVE_ITEM;
    });

    /** Updates the collapsed state whenever the user toggles the sidebar. */
    protected onCollapsedChanged(event: Event): void {
        this.collapsed.set((event as CustomEvent<boolean>).detail);
    }

    /** Scopes the application to one country, or to all of them with `null`. */
    protected selectCountry(country: Country | null): void {
        this.countryScope.select(country);
    }

    /** `sh-vertical-nav` clips its items instead of scrolling them and exposes no part to style that. */
    private async makeNavContentScrollable(): Promise<void> {
        const nav = this.nav().nativeElement as HTMLElement & {
            updateComplete?: Promise<unknown>;
            updateHighlight?: () => void;
        };
        await nav.updateComplete;
        const shadowRoot = nav.shadowRoot;
        const content = shadowRoot?.querySelector<HTMLElement>('.vertical-nav__content');
        const highlight = shadowRoot?.querySelector<HTMLElement>('.vertical-nav__highlight');
        if (!shadowRoot || !content || !highlight) {
            return;
        }

        const sheet = new CSSStyleSheet();
        sheet.replaceSync(`
            :host .vertical-nav__content,
            :host(:not([overflow])) .vertical-nav__content,
            :host([collapsed]) .vertical-nav__content {
                overflow-x: hidden;
                overflow-y: auto;
                scrollbar-width: none;
            }
            .vertical-nav__content::-webkit-scrollbar {
                display: none;
            }
        `);
        shadowRoot.adoptedStyleSheets = [...shadowRoot.adoptedStyleSheets, sheet];

        // The highlight lives outside the scroll area, so it has to follow the active item manually.
        content.addEventListener(
            'scroll',
            () => {
                highlight.style.transition = 'none';
                nav.updateHighlight?.();
                const active = nav.querySelector('sh-vertical-nav-item[active]');
                if (active) {
                    const itemRect = active.getBoundingClientRect();
                    const contentRect = content.getBoundingClientRect();
                    const visible = itemRect.bottom > contentRect.top && itemRect.top < contentRect.bottom;
                    highlight.style.opacity = visible ? '1' : '0';
                }
                requestAnimationFrame(() => (highlight.style.transition = ''));
            },
            { passive: true }
        );
    }
}
